// src/utils/metaPixel.ts

import axios from "axios";
import crypto from "crypto";
import { v4 as uuidv4 } from "uuid";
import { safeStringify } from "@/utils/api";

const isBrowser = typeof window !== "undefined";
const PIXEL_ID = process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID || process.env.FACEBOOK_PIXEL_ID || "";
const TEST_EVENT_CODE =
  process.env.NEXT_PUBLIC_FACEBOOK_TEST_EVENT_CODE || process.env.FACEBOOK_TEST_EVENT_CODE || "";

export const initializeMetaPixel = () => {
  if (!isBrowser) return; // No-op on server
  if (!PIXEL_ID) {
    if (process.env.NODE_ENV === "development") {
      console.warn("Meta Pixel disabled: PIXEL_ID not configured");
    }
    return;
  }
  import("react-facebook-pixel")
    .then((module) => {
      const ReactPixel = module.default;
      ReactPixel.init(PIXEL_ID);
      ReactPixel.pageView();
    })
    .catch((err) => console.error("Error initializing Meta Pixel:", safeStringify(err)));
};

export const hashData = (data: string): string => {
  return crypto.createHash("sha256").update(data).digest("hex");
};

const sendServerSideEvent = async (eventName: string, eventData: any) => {
  try {
    const event_id = uuidv4();
    if (process.env.NODE_ENV === "development") {
      console.debug("Sending server-side event:", { eventName, event_id });
    }

    if (!PIXEL_ID) {
      if (process.env.NODE_ENV === "development") {
        console.warn("Meta CAPI skipped: PIXEL_ID not configured");
      }
      return;
    }

    // When running server-side, Axios requires an absolute URL.
    // Prefer NEXT_PUBLIC_SITE_URL, else NEXTAUTH_URL, else fall back to localhost.
    const baseUrl = isBrowser
      ? ""
      : process.env.NEXT_PUBLIC_SITE_URL ||
        process.env.NEXTAUTH_URL ||
        `http://localhost:${process.env.NEXT_APP_PORT || 3000}`;
    const url = isBrowser
      ? "/api/meta-conversion"
      : `${(baseUrl as string).replace(/\/$/, "")}/api/meta-conversion`;

    const response = await axios.post(url, {
      eventName,
      eventData,
      event_id,
      // pass test event code when available for validation in Events Manager
      test_event_code: TEST_EVENT_CODE || undefined,
    });

    if (process.env.NODE_ENV === "development") {
      console.debug("Server-side event response:", response.data);
    }
  } catch (error) {
    // Do not throw; logging only to avoid impacting UX
    console.error("Error sending server-side event:", safeStringify(error));
    if (axios.isAxiosError(error)) {
      if (error.response) {
        console.error("Meta API Error Response:", safeStringify(error.response.data));
      } else if (error.request) {
        console.error(
          "Meta API No Response, request config:",
          safeStringify({ url: error.config?.url }),
        );
      }
    }
  }
};

export const getCookie = (cookieName: string): string | null => {
  if (!isBrowser) return null;
  const name = cookieName + "=";
  const decodedCookie = decodeURIComponent(document.cookie);
  const cookieArray = decodedCookie.split(";");
  for (let i = 0; i < cookieArray.length; i++) {
    const cookie = cookieArray[i].trim();
    if (cookie.indexOf(name) === 0) {
      return cookie.substring(name.length, cookie.length);
    }
  }
  return null;
};

export const trackViewContent = (contentName: string, url: string, userEmail: string) => {
  const fbp = getCookie("_fbp");
  const fbc = getCookie("_fbc");

  const eventData = {
    event_name: "ViewContent",
    event_time: Math.floor(Date.now() / 1000),
    action_source: "website",
    event_source_url: url,
    user_data: {
      em: [hashData(userEmail.toLowerCase())],
      fbp,
      fbc,
    },
    custom_data: {
      content_name: contentName,
    },
  };
  sendServerSideEvent("ViewContent", eventData);
};

export const trackPurchase = (
  value: number,
  currency: string,
  transactionId: string,
  userEmail: string,
) => {
  const fbp = getCookie("_fbp");
  const fbc = getCookie("_fbc");

  const eventData = {
    event_name: "Purchase",
    event_time: Math.floor(Date.now() / 1000),
    action_source: "website",
    event_source_url: isBrowser ? window.location.href : "",
    user_data: {
      em: [hashData(userEmail.toLowerCase())],
      fbp,
      fbc,
    },
    custom_data: {
      currency,
      value,
      transaction_id: transactionId,
    },
  };
  sendServerSideEvent("Purchase", eventData);
};

export const trackCompleteRegistration = (userEmail: string) => {
  const fbp = getCookie("_fbp");
  const fbc = getCookie("_fbc");

  const eventData = {
    event_name: "CompleteRegistration",
    event_time: Math.floor(Date.now() / 1000),
    action_source: "website",
    event_source_url: isBrowser ? window.location.href : "",
    user_data: {
      em: [hashData(userEmail.toLowerCase())],
      fbp,
      fbc,
    },
  };
  sendServerSideEvent("CompleteRegistration", eventData);
};
